import React, { useState } from "react";
import { toast } from "sonner";
import { Store, Loader2, Building2, Users, CheckCircle2, Wrench } from "lucide-react";
import { marketplaceApi } from "@/services/api";
import { fmtMoney, apiError } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

// Tenant control on a remedial quote: keep the works in-house or put them out
// to vetted marketplace contractors. Creates/loads the Remedial Lead lazily.
export default function MarketplaceQuoteCard({ quoteId }) {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadLead = async () => {
    setLoading(true);
    try {
      const res = await marketplaceApi.leadFromQuote(quoteId);
      setLead(res.data);
    } catch (e) {
      toast.error(apiError(e, "Could not open marketplace options"));
    } finally {
      setLoading(false);
    }
  };

  const decide = async (decision) => {
    if (!lead) return;
    setBusy(true);
    try {
      const res = await marketplaceApi.leadDecision(lead.id, decision);
      setLead(res.data);
      toast.success(decision === "marketplace"
        ? `Put to the marketplace — ${res.data.matched_count || 0} contractor(s) matched`
        : "Marked as in-house");
    } catch (e) {
      toast.error(apiError(e, "Could not update"));
    } finally {
      setBusy(false);
    }
  };

  const matched = (lead?.matched_contractor_ids || []).length;
  const decision = lead?.decision;

  return (
    <Card className="p-5" data-testid="quote-marketplace-card">
      <div className="flex items-start gap-3">
        <Store className="mt-0.5 h-5 w-5 shrink-0 text-red-600" />
        <div className="min-w-0 flex-1">
          <h3 className="font-semibold text-slate-800">Remedial works — do it yourself or put to the marketplace</h3>
          <p className="mt-0.5 text-sm text-slate-500">Let vetted fire-door maintenance contractors in the area quote for these works, or keep them in-house.</p>

          {!lead && (
            <Button className="mt-3" variant="outline" size="sm" onClick={loadLead} disabled={loading} data-testid="quote-marketplace-open">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Store className="mr-2 h-4 w-4" />}
              Review marketplace options
            </Button>
          )}

          {lead && (
            <div className="mt-3 space-y-3">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
                <span className="text-slate-500">Estimated works value</span>
                <span className="font-semibold text-slate-900" data-testid="quote-marketplace-value">{fmtMoney(lead.estimated_value)}</span>
                <span className="inline-flex items-center gap-1 text-slate-500"><Building2 className="h-3.5 w-3.5" />{lead.building_postcode || "no building postcode"}</span>
              </div>

              {decision === "marketplace" ? (
                <div className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800" data-testid="quote-marketplace-published">
                  <p className="flex items-center gap-1.5 font-medium"><Users className="h-4 w-4" /> On the marketplace — {matched} approved contractor(s) matched in this area.</p>
                  {matched === 0 && <p className="mt-1 text-xs text-emerald-700">No approved contractors cover this postcode yet. Add coverage from Super Admin → Marketplace.</p>}
                  <Button variant="ghost" size="sm" className="mt-1 h-7 px-2 text-emerald-700" onClick={() => decide("self")} disabled={busy} data-testid="quote-marketplace-switch-self">
                    Switch to in-house
                  </Button>
                </div>
              ) : decision === "self" ? (
                <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-800" data-testid="quote-marketplace-self">
                  <p className="flex items-center gap-1.5 font-medium"><Wrench className="h-4 w-4" /> Keeping these works in-house.</p>
                  <Button variant="ghost" size="sm" className="mt-1 h-7 px-2 text-blue-700" onClick={() => decide("marketplace")} disabled={busy} data-testid="quote-marketplace-switch-mp">
                    Put out to the marketplace instead
                  </Button>
                </div>
              ) : (
                <div className="flex flex-wrap gap-2">
                  <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => decide("marketplace")} disabled={busy} data-testid="quote-marketplace-put">
                    {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Store className="mr-2 h-4 w-4" />} Put out to the marketplace
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => decide("self")} disabled={busy} data-testid="quote-marketplace-inhouse">
                    <CheckCircle2 className="mr-2 h-4 w-4" /> We&apos;ll do it ourselves
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}
